import React, { useState, useEffect } from 'react';
import { UserRole } from '@adjust/dashboard-core/api/types';
import { useDeprecationMiddleware } from '@adjust/dashboard-components/build/deprecation/reactWithoutRouterDeprecationMiddleware';

import { RequestStatus } from '../../types';
import { redirectToLogin } from '../../helpers/utilities';
import { initTracking, startTracking } from '../../helpers/amplitude';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useApps } from '../../hooks/useApps';
import { useLinks } from '../../hooks/useLinks';
import { NoData } from './NoData';
import { Header } from './Header';
import { Content } from './Content';

export const Main: React.FC = () => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.idle);
  const [debugMessage, setDebugMessage] = useState<string>();
  const currentUser = useCurrentUser();
  const apps = useApps();
  const links = useLinks({
    fetchApp: apps.fetchDetails,
  });

  const { account, user } = currentUser;
  useDeprecationMiddleware(account, user, {
    projectName: 'deeplink-generator',
  });

  const handleInvalidResponse = () => {
    if (process.env.NODE_ENV === 'production') {
      redirectToLogin();
      return;
    }

    setStatus(RequestStatus.failed);
  };

  useEffect(() => {
    initTracking();
    setStatus(RequestStatus.fetching);

    Promise.all([currentUser.fetch(), apps.fetch()])
      .then(([{ user }]) => {
        if (user.role === UserRole.Custom) {
          const availableTokens = user.local_roles.mobile_apps
            .filter(app => app.role === UserRole.Editor)
            .map(app => app.token);

          if (availableTokens.length === 0) {
            setDebugMessage('You are not editor of any app.');
            handleInvalidResponse();
            return;
          }

          apps.setAvailableTokens(availableTokens);
        } else if (![UserRole.Admin, UserRole.Editor].includes(user.role)) {
          setDebugMessage(
            `Your role is ${user.role}. Only users with admin or editor roles have access to deeplink generator.`,
          );
          handleInvalidResponse();
          return;
        }

        startTracking(user);
        return links.migrateData(user);
      })
      .then(() => setStatus(RequestStatus.fetched))
      .catch(() => {
        setDebugMessage('You are probably not logged-in to adjust dashboard.');
        handleInvalidResponse();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if ([RequestStatus.idle, RequestStatus.fetching].includes(status)) {
    return <div className="main-loading" />;
  }

  if (status === RequestStatus.failed) {
    return (
      <NoData title="Something went wrong">
        <p>{debugMessage}</p>
      </NoData>
    );
  }

  return (
    <>
      <Header currentUser={currentUser} />
      <Content apps={apps} links={links} />
    </>
  );
};
